import * as z from 'zod'
import type { StaffRole } from './staffRoles'

// Public suggestions board (guides/31).
// Anyone can browse; signed-in users with a username can post, vote and
// comment. This module is the single source of truth for categories,
// statuses, limits and validation, shared by the API and the UI.

export const SUGGESTION_CATEGORIES = [
  { value: 'customization', label: 'Customization', icon: 'i-lucide-paintbrush' },
  { value: 'content', label: 'Links & content', icon: 'i-lucide-link' },
  { value: 'integrations', label: 'Integrations', icon: 'i-lucide-plug' },
  { value: 'skins', label: 'Skins', icon: 'i-lucide-palette' },
  { value: 'statistics', label: 'Statistics', icon: 'i-lucide-chart-line' },
  { value: 'account', label: 'Account & billing', icon: 'i-lucide-user-cog' },
  { value: 'other', label: 'Other', icon: 'i-lucide-lightbulb' }
] as const

export type SuggestionCategory = typeof SUGGESTION_CATEGORIES[number]['value']

export const SUGGESTION_STATUSES = [
  { value: 'open', label: 'Open', color: 'neutral', icon: 'i-lucide-circle-dot' },
  { value: 'planned', label: 'Planned', color: 'info', icon: 'i-lucide-calendar-check' },
  { value: 'in_progress', label: 'In progress', color: 'warning', icon: 'i-lucide-loader' },
  { value: 'completed', label: 'Completed', color: 'success', icon: 'i-lucide-circle-check' },
  { value: 'declined', label: 'Declined', color: 'error', icon: 'i-lucide-circle-x' }
] as const

export type SuggestionStatus = typeof SUGGESTION_STATUSES[number]['value']

/**
 * Roles that moderate the board: edit or delete any suggestion, delete any
 * comment, set the status. Moderators are not included; like every staff
 * role they only get the "Team" badge on their comments.
 */
export const SUGGESTION_MODERATOR_ROLES: readonly StaffRole[] = ['admin', 'manager']

export function canModerateSuggestions(role: StaffRole | null | undefined): boolean {
  return !!role && SUGGESTION_MODERATOR_ROLES.includes(role)
}

export const SUGGESTION_SORTS = ['top', 'new'] as const
export type SuggestionSort = typeof SUGGESTION_SORTS[number]

/** -1 = downvote, 0 = no vote, 1 = upvote. */
export type SuggestionVoteValue = -1 | 0 | 1

export const SUGGESTIONS_PAGE_SIZE = 20
export const SUGGESTION_COMMENTS_PAGE_SIZE = 20
/** List rows carry a shortened description; the detail page has the full text. */
export const SUGGESTION_EXCERPT_LENGTH = 240

export const SUGGESTION_TITLE_MIN = 5
export const SUGGESTION_TITLE_MAX = 120
export const SUGGESTION_DESCRIPTION_MAX = 2000
export const SUGGESTION_COMMENT_MAX = 1000

const CATEGORY_VALUES = SUGGESTION_CATEGORIES.map(c => c.value) as [SuggestionCategory, ...SuggestionCategory[]]
const STATUS_VALUES = SUGGESTION_STATUSES.map(s => s.value) as [SuggestionStatus, ...SuggestionStatus[]]

export function getSuggestionCategory(value: string) {
  return SUGGESTION_CATEGORIES.find(c => c.value === value) ?? SUGGESTION_CATEGORIES[SUGGESTION_CATEGORIES.length - 1]!
}

export function getSuggestionStatus(value: string) {
  return SUGGESTION_STATUSES.find(s => s.value === value) ?? SUGGESTION_STATUSES[0]
}

export function isSuggestionCategory(value: unknown): value is SuggestionCategory {
  return typeof value === 'string' && (CATEGORY_VALUES as string[]).includes(value)
}

export function isSuggestionStatus(value: unknown): value is SuggestionStatus {
  return typeof value === 'string' && (STATUS_VALUES as string[]).includes(value)
}

export interface SuggestionAuthor {
  id: string
  username: string | null
  display_name: string | null
  avatar_url: string | null
}

export interface Suggestion {
  id: string
  title: string
  /** Full text on the detail endpoint, an excerpt in list responses. */
  description: string
  category: SuggestionCategory
  status: SuggestionStatus
  upvotes: number
  downvotes: number
  /** upvotes - downvotes. The board ranks on this. */
  score: number
  comment_count: number
  author: SuggestionAuthor
  /** The signed-in viewer's vote, 0 when signed out or not voted. */
  my_vote: SuggestionVoteValue
  created_at: string
  updated_at: string
}

export interface SuggestionComment {
  id: string
  content: string
  /** Snapshot of the author's staff role at posting time. */
  is_staff: boolean
  author: SuggestionAuthor
  created_at: string
}

export interface SuggestionVoteResult {
  upvotes: number
  downvotes: number
  score: number
  my_vote: SuggestionVoteValue
}

/** Body of create/update, also the editor form schema. */
export const suggestionInputSchema = z.object({
  title: z
    .string()
    .trim()
    .min(SUGGESTION_TITLE_MIN, `Use at least ${SUGGESTION_TITLE_MIN} characters`)
    .max(SUGGESTION_TITLE_MAX, `Keep the title under ${SUGGESTION_TITLE_MAX} characters`),
  description: z
    .string()
    .trim()
    .max(SUGGESTION_DESCRIPTION_MAX, `Keep the description under ${SUGGESTION_DESCRIPTION_MAX} characters`),
  category: z.enum(CATEGORY_VALUES, { error: 'Pick a category' })
})

export type SuggestionInput = z.output<typeof suggestionInputSchema>

export const suggestionCommentSchema = z.object({
  content: z
    .string()
    .trim()
    .min(1, 'Write a comment first')
    .max(SUGGESTION_COMMENT_MAX, `Keep comments under ${SUGGESTION_COMMENT_MAX} characters`)
})

export const suggestionVoteSchema = z.object({
  value: z.union([z.literal(-1), z.literal(0), z.literal(1)])
})

export const suggestionStatusSchema = z.object({
  status: z.enum(STATUS_VALUES)
})

/** Query of the list endpoint. Unknown values fall back to the defaults. */
export const suggestionListQuerySchema = z.object({
  category: z.enum(CATEGORY_VALUES).optional().catch(undefined),
  status: z.enum(STATUS_VALUES).optional().catch(undefined),
  sort: z.enum(SUGGESTION_SORTS).catch('top'),
  page: z.coerce.number().int().min(1).max(1000).catch(1),
  limit: z.coerce.number().int().min(1).max(50).catch(SUGGESTIONS_PAGE_SIZE)
})

export type SuggestionListQuery = z.output<typeof suggestionListQuerySchema>

/** "+5", "0" or "-3". */
export function formatSuggestionScore(score: number): string {
  return score > 0 ? `+${score}` : String(score)
}

export function suggestionExcerpt(description: string): string {
  if (description.length <= SUGGESTION_EXCERPT_LENGTH) return description
  return `${description.slice(0, SUGGESTION_EXCERPT_LENGTH).trimEnd()}...`
}
